import { useState, useEffect, useCallback } from 'react';
import {
  Loader2, AlertTriangle, CheckCircle2, XCircle, Plus,
  RefreshCw, ClipboardCheck, Truck, Package,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';
import { IqcReadingsSheet } from './IqcReadingsSheet';

// ── Types ─────────────────────────────────────────────────────────────────────

// Backend `/manufacturing-flow/orders` returns `{ id, number, product: { name }, status }`.
// The old shape here assumed flat `orderNumber` + `productName` fields which
// never existed → Ishan F-01 dropdown rendered blank options. Match the real
// payload.
interface ProductionOrder {
  id: string;
  number: string;
  product?: { name?: string } | null;
  status: string;
}

interface RMInspection {
  id: string;
  productionOrderId: string;
  productionOrderNumber?: string;
  materialName: string;
  purchaseOrderRef: string;
  supplierName: string;
  receivedQty: number;
  poQty: number;
  gradeExpected: string;
  gradeActual: string;
  thicknessExpected: number;
  thicknessActual: number;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string | null;
  vendorReturnNote?: string | null;
  grnIssueRaised?: boolean;
  grnNumber?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const THICKNESS_TOLERANCE = 0.05; // 5%

function thicknessPass(expected: number, actual: number): boolean {
  if (!expected) return true;
  return Math.abs(actual - expected) / expected <= THICKNESS_TOLERANCE;
}

function gradePass(expected: string, actual: string): boolean {
  return expected.trim().toUpperCase() === actual.trim().toUpperCase();
}

// ── Reject Modal ──────────────────────────────────────────────────────────────

interface RejectModalProps {
  inspection: RMInspection;
  onClose: () => void;
  onSubmit: (data: {
    rejectionReason: string;
    vendorReturnNote: string;
    grnIssueRaised: boolean;
    grnNumber: string;
  }) => Promise<void>;
}

function RejectModal({ inspection, onClose, onSubmit }: RejectModalProps) {
  const [rejectionReason, setRejectionReason] = useState('');
  const [vendorReturnNote, setVendorReturnNote] = useState('');
  const [grnIssueRaised, setGrnIssueRaised] = useState(false);
  const [grnNumber, setGrnNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!rejectionReason.trim()) {
      setError('Rejection reason is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ rejectionReason, vendorReturnNote, grnIssueRaised, grnNumber });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-[var(--bg-surface)] border border-[var(--border-primary)] shadow-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-[var(--text-primary)] flex items-center gap-2">
            <XCircle size={18} className="text-red-500" />
            Reject Inspection
          </h3>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
            ✕
          </button>
        </div>

        <p className="text-xs text-[var(--text-muted)] mb-4">
          Material: <strong>{inspection.materialName}</strong> — Supplier: <strong>{inspection.supplierName}</strong>
        </p>

        <div className="space-y-4">
          {/* Rejection Reason */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
              Rejection Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Describe why this RM is being rejected…"
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-3)]"
            />
          </div>

          {/* Vendor Return Note */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
              Vendor Return Note
            </label>
            <textarea
              rows={2}
              value={vendorReturnNote}
              onChange={(e) => setVendorReturnNote(e.target.value)}
              placeholder="Instructions for returning material to vendor…"
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-3)]"
            />
          </div>

          {/* GRN Issue */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="grnIssue"
              checked={grnIssueRaised}
              onChange={(e) => setGrnIssueRaised(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-[var(--ink-0)]"
            />
            <label htmlFor="grnIssue" className="text-sm text-[var(--text-secondary)]">
              Raise GRN Issue
            </label>
          </div>

          {/* GRN Number */}
          {grnIssueRaised && (
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                GRN Number
              </label>
              <input
                type="text"
                value={grnNumber}
                onChange={(e) => setGrnNumber(e.target.value)}
                placeholder="GRN-XXXX"
                className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-3)]"
              />
            </div>
          )}

          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
            Confirm Reject
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Inspection Badge ──────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const cfg: Record<string, string> = {
    pending:  'bg-yellow-100 text-yellow-700',
    approved: 'bg-green-100  text-green-700',
    rejected: 'bg-red-100    text-red-700',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// ── Create Form ───────────────────────────────────────────────────────────────

interface CreateFormProps {
  orders: ProductionOrder[];
  onCreated: () => void;
}

function CreateInspectionForm({ orders, onCreated }: CreateFormProps) {
  const [form, setForm] = useState({
    productionOrderId:   '',
    materialName:        '',
    supplierName:        '',
    purchaseOrderRef:    '',
    receivedQty:         '',
    poQty:               '',
    gradeExpected:       '',
    gradeActual:         '',
    thicknessExpected:   '',
    thicknessActual:     '',
    notes:               '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const set = (field: keyof typeof form, val: string) => {
    setForm((prev) => ({ ...prev, [field]: val }));
    setSuccess(false);
    setError(null);
  };

  // Auto pass/fail indicators
  const gGradePass = form.gradeExpected && form.gradeActual
    ? gradePass(form.gradeExpected, form.gradeActual) : null;
  const gThickPass = form.thicknessExpected && form.thicknessActual
    ? thicknessPass(Number(form.thicknessExpected), Number(form.thicknessActual)) : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.productionOrderId || !form.materialName || !form.supplierName) {
      setError('Production order, material name, and supplier are required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/manufacturing-flow/rm-inspections', {
        productionOrderId:  form.productionOrderId,
        materialName:       form.materialName,
        purchaseOrderRef:   form.purchaseOrderRef,
        supplierName:       form.supplierName,
        receivedQty:        Number(form.receivedQty) || 0,
        poQty:              Number(form.poQty) || 0,
        gradeExpected:      form.gradeExpected,
        gradeActual:        form.gradeActual,
        thicknessExpected:  Number(form.thicknessExpected) || 0,
        thicknessActual:    Number(form.thicknessActual) || 0,
        notes:              form.notes || null,
      });
      setSuccess(true);
      setForm({
        productionOrderId: '', materialName: '', supplierName: '', purchaseOrderRef: '',
        receivedQty: '', poQty: '', gradeExpected: '', gradeActual: '',
        thicknessExpected: '', thicknessActual: '', notes: '',
      });
      onCreated();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create inspection');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-3)]';
  const labelCls = 'block text-xs font-medium text-[var(--text-secondary)] mb-1';

  return (
    <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-5">
      <h2 className="text-base font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
        <Plus size={16} className="text-[var(--ink-0)]" />
        Create RM Inspection
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Production Order */}
        <div>
          <label className={labelCls}>Production Order <span className="text-red-500">*</span></label>
          <select
            value={form.productionOrderId}
            onChange={(e) => set('productionOrderId', e.target.value)}
            className={inputCls}
          >
            <option value="">Select order…</option>
            {orders.map((o) => (
              <option key={o.id} value={o.id}>
                {o.number} — {o.product?.name ?? 'Unknown product'}
              </option>
            ))}
          </select>
        </div>

        {/* Material & Supplier */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Material Name <span className="text-red-500">*</span></label>
            <input type="text" value={form.materialName} onChange={(e) => set('materialName', e.target.value)} placeholder="e.g. SS304 Sheet" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Supplier Name <span className="text-red-500">*</span></label>
            <input type="text" value={form.supplierName} onChange={(e) => set('supplierName', e.target.value)} placeholder="Supplier Ltd." className={inputCls} />
          </div>
        </div>

        {/* PO Ref */}
        <div>
          <label className={labelCls}>Purchase Order Ref</label>
          <input type="text" value={form.purchaseOrderRef} onChange={(e) => set('purchaseOrderRef', e.target.value)} placeholder="PO-XXXX" className={inputCls} />
        </div>

        {/* Quantities */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Received Qty</label>
            <input type="number" min={0} value={form.receivedQty} onChange={(e) => set('receivedQty', e.target.value)} placeholder="0" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>PO Qty</label>
            <input type="number" min={0} value={form.poQty} onChange={(e) => set('poQty', e.target.value)} placeholder="0" className={inputCls} />
          </div>
        </div>

        {/* Grade */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Grade Expected</label>
            <input type="text" value={form.gradeExpected} onChange={(e) => set('gradeExpected', e.target.value)} placeholder="SS304" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Grade Actual</label>
            <div className="relative">
              <input type="text" value={form.gradeActual} onChange={(e) => set('gradeActual', e.target.value)} placeholder="SS304" className={inputCls} />
              {gGradePass !== null && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2">
                  {gGradePass
                    ? <CheckCircle2 size={16} className="text-green-500" />
                    : <XCircle size={16} className="text-red-500" />}
                </span>
              )}
            </div>
            {gGradePass === false && (
              <p className="mt-0.5 text-[10px] text-red-500">Grade mismatch!</p>
            )}
          </div>
        </div>

        {/* Thickness */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Thickness Expected (mm)</label>
            <input type="number" step="0.01" value={form.thicknessExpected} onChange={(e) => set('thicknessExpected', e.target.value)} placeholder="0.00" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Thickness Actual (mm)</label>
            <div className="relative">
              <input type="number" step="0.01" value={form.thicknessActual} onChange={(e) => set('thicknessActual', e.target.value)} placeholder="0.00" className={inputCls} />
              {gThickPass !== null && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2">
                  {gThickPass
                    ? <CheckCircle2 size={16} className="text-green-500" />
                    : <XCircle size={16} className="text-red-500" />}
                </span>
              )}
            </div>
            {gThickPass === false && (
              <p className="mt-0.5 text-[10px] text-red-500">Thickness variance &gt;5% — may fail.</p>
            )}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className={labelCls}>Notes</label>
          <textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Additional observations…" className={inputCls} />
        </div>

        {error && <p className="text-xs text-red-500">{error}</p>}
        {success && (
          <p className="flex items-center gap-1.5 text-xs text-green-600">
            <CheckCircle2 size={14} /> Inspection created successfully
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full flex items-center justify-center gap-2 rounded-lg bg-[var(--ink-0)] py-2.5 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-60"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <ClipboardCheck size={16} />}
          {submitting ? 'Submitting…' : 'Submit Inspection'}
        </button>
      </form>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function RMInspectionPage() {
  const [orders, setOrders] = useState<ProductionOrder[]>([]);
  const [inspections, setInspections] = useState<RMInspection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<RMInspection | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [ordersRes, inspRes] = await Promise.all([
        api.get('/manufacturing-flow/orders'),
        api.get('/manufacturing-flow/rm-inspections'),
      ]);
      const ordersData = ordersRes.data.data ?? ordersRes.data;
      const inspData   = inspRes.data.data ?? inspRes.data;
      setOrders(Array.isArray(ordersData) ? ordersData : ordersData.orders ?? []);
      setInspections(Array.isArray(inspData) ? inspData : inspData.inspections ?? []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleApprove = async (id: string) => {
    setActionLoading(id);
    try {
      await api.patch(`/manufacturing-flow/rm-inspections/${id}/approve`, { approved: true });
      await fetchAll();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Approval failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectSubmit = async (id: string, data: {
    rejectionReason: string;
    vendorReturnNote: string;
    grnIssueRaised: boolean;
    grnNumber: string;
  }) => {
    await api.patch(`/manufacturing-flow/rm-inspections/${id}/approve`, {
      approved: false,
      rejectionReason:  data.rejectionReason,
      vendorReturnNote: data.vendorReturnNote || null,
      grnIssueRaised:   data.grnIssueRaised,
      grnNumber:        data.grnNumber || null,
    });
    await fetchAll();
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-[var(--text-muted)]">Loading inspections…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-lg py-24 text-center">
        <AlertTriangle size={36} className="mx-auto mb-3 text-red-400" />
        <p className="text-sm text-red-600 mb-4">{error}</p>
        <button onClick={fetchAll} className="rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm hover:bg-[var(--bg-inset)]">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)] flex items-center gap-2">
            <Package size={22} className="text-[var(--ink-0)]" />
            RM Inspection
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-0.5">Grade verification, thickness check, and approval workflow</p>
        </div>
        <button
          onClick={fetchAll}
          className="flex items-center gap-1.5 rounded-lg border border-[var(--border-primary)] px-3 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]"
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* Phase 2 plan 04 — IQC readings tied to the GRN lot (mockup X7). The production-order form below is unchanged. */}
      <IqcReadingsSheet />

      {/* Two-panel layout */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Create Form */}
        <CreateInspectionForm orders={orders} onCreated={fetchAll} />

        {/* Inspection List */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-5">
          <h2 className="text-base font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
            <ClipboardCheck size={16} className="text-[var(--ink-0)]" />
            Inspection List
            <span className="ml-auto text-xs text-[var(--text-muted)]">{inspections.length} records</span>
          </h2>

          <div className="space-y-3 overflow-y-auto max-h-[700px] pr-1">
            {inspections.length === 0 ? (
              <p className="py-12 text-center text-sm text-[var(--text-muted)]">No inspections yet</p>
            ) : (
              inspections.map((insp) => {
                const gPass = insp.gradeExpected && insp.gradeActual
                  ? gradePass(insp.gradeExpected, insp.gradeActual) : null;
                const tPass = insp.thicknessExpected && insp.thicknessActual
                  ? thicknessPass(insp.thicknessExpected, insp.thicknessActual) : null;

                return (
                  <div
                    key={insp.id}
                    className={`rounded-lg border p-4 ${
                      insp.status === 'approved' ? 'border-green-200 bg-green-50' :
                      insp.status === 'rejected' ? 'border-red-200 bg-red-50' :
                      'border-[var(--border-primary)] bg-[var(--bg-muted)]'
                    }`}
                  >
                    {/* Top row */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <p className="text-sm font-semibold text-[var(--text-primary)]">{insp.materialName}</p>
                        <p className="text-xs text-[var(--text-muted)]">
                          {insp.supplierName} · PO: {insp.purchaseOrderRef || '—'}
                        </p>
                      </div>
                      <StatusBadge status={insp.status} />
                    </div>

                    {/* Checks */}
                    <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
                      <div className="flex items-center gap-1">
                        {gPass === true  && <CheckCircle2 size={13} className="text-green-500" />}
                        {gPass === false && <XCircle size={13} className="text-red-500" />}
                        <span className="text-[var(--text-muted)]">Grade: {insp.gradeExpected || '—'} / {insp.gradeActual || '—'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {tPass === true  && <CheckCircle2 size={13} className="text-green-500" />}
                        {tPass === false && <XCircle size={13} className="text-red-500" />}
                        <span className="text-[var(--text-muted)]">Thick: {insp.thicknessExpected || '—'} / {insp.thicknessActual || '—'} mm</span>
                      </div>
                      <div className="text-[var(--text-muted)]">Recv: {insp.receivedQty} / PO: {insp.poQty}</div>
                      <div className="text-[var(--text-muted)]">{formatDate(insp.createdAt)}</div>
                    </div>

                    {/* Rejection info */}
                    {insp.status === 'rejected' && (
                      <div className="mt-1 rounded bg-red-100 p-2 text-xs text-red-700 space-y-0.5">
                        {insp.rejectionReason && <p><strong>Reason:</strong> {insp.rejectionReason}</p>}
                        {insp.vendorReturnNote && <p className="flex items-center gap-1"><Truck size={11} /> {insp.vendorReturnNote}</p>}
                        {insp.grnIssueRaised && <p>GRN Issue Raised {insp.grnNumber ? `— ${insp.grnNumber}` : ''}</p>}
                      </div>
                    )}

                    {/* Actions for pending */}
                    {insp.status === 'pending' && (
                      <div className="flex items-center gap-2 mt-3">
                        <button
                          onClick={() => handleApprove(insp.id)}
                          disabled={actionLoading === insp.id}
                          className="flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-60"
                        >
                          {actionLoading === insp.id
                            ? <Loader2 size={12} className="animate-spin" />
                            : <CheckCircle2 size={12} />}
                          Approve
                        </button>
                        <button
                          onClick={() => setRejectTarget(insp)}
                          disabled={actionLoading === insp.id}
                          className="flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-60"
                        >
                          <XCircle size={12} />
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {rejectTarget && (
        <RejectModal
          inspection={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onSubmit={(data) => handleRejectSubmit(rejectTarget.id, data)}
        />
      )}
    </div>
  );
}
